import { useSelector } from "react-redux";

import { countryCodeOrDefault, DEFAULT_COUNTRY_CODE } from "@/config/countries";
import { PLATFORM_BRAND } from "@/config/platformBrand";
import type { RootState } from "@/redux/store";

export type LoginBranding = {
  tenantKey: string;
  logoSrc: string;
  logoAlt: string;
  schoolName: string;
  helperText?: string;
  /** Tenant's login page image. Undefined when unset — the panel then keeps its plain background. */
  heroImageSrc?: string;
  /**
   * Sign-in methods this tenant offers. A rendering hint from the anonymous resolve response —
   * the server independently decides what it will accept, so trimming this list here only removes
   * buttons, it never grants anything.
   */
  authMethods: string[];
  /** The school's ISO 3166-1 alpha-2 country, used to default the phone country selector. */
  countryCode: string;
  /** Tenant's own login heading, or undefined to use the default copy. */
  loginTitle?: string;
  /** Tenant's own login sub-heading, or undefined to use the default copy. */
  loginSubtitle?: string;
};

const DEFAULT_TENANT_KEY = "default";

/** Matches TenantAuthMethods.Otp on the server. */
export const OTP_METHOD = "otp";

/** Matches TenantAuthMethods.Password on the server. */
export const PASSWORD_METHOD = "password";

/** Matches TenantAuthMethods.Google on the server. */
export const GOOGLE_METHOD = "google";

// Fallback branding for unmapped hosts (local dev, the platform console, marketing domains) and for
// the moment before the host -> tenant lookup returns. This used to be one customer's logo and the
// literal word "School", which meant an unmapped host showed RGPS's brand to everyone — worse than
// showing nothing, because it is another school's identity.
const DEFAULT_LOGO_SRC = PLATFORM_BRAND.logoSrc;
const DEFAULT_SCHOOL_NAME = PLATFORM_BRAND.name;
const DEFAULT_HELPER_TEXT = "Powered by BrightHub";

/**
 * Login-screen branding, sourced from the tenant resolved for the current
 * hostname (see src/features/hostTenant). Falls back to default branding
 * for unmapped hosts or while resolution is still in flight.
 */
export function useLoginBranding(): LoginBranding {
  const tenant = useSelector((state: RootState) => state.hostTenant.tenant);

  if (!tenant) {
    return {
      tenantKey: DEFAULT_TENANT_KEY,
      logoSrc: DEFAULT_LOGO_SRC,
      logoAlt: "School logo",
      schoolName: DEFAULT_SCHOOL_NAME,
      helperText: DEFAULT_HELPER_TEXT,
      // An unmapped host (localhost, the platform console) is not a tenant, so there is no tenant
      // configuration to honour — the platform's own OTP login applies.
      authMethods: [OTP_METHOD],
      countryCode: DEFAULT_COUNTRY_CODE,
    };
  }

  return {
    tenantKey: tenant.tenantId || DEFAULT_TENANT_KEY,
    logoSrc: tenant.logoUrl || DEFAULT_LOGO_SRC,
    logoAlt: "School logo",
    schoolName: tenant.tenantName || DEFAULT_SCHOOL_NAME,
    helperText: DEFAULT_HELPER_TEXT,
    // No platform-wide fallback image: an unset login image means the existing
    // gradient-only panel, not someone else's photo.
    heroImageSrc: tenant.loginHeroImageUrl || undefined,
    authMethods: tenant.authMethods?.length ? tenant.authMethods : [OTP_METHOD],
    countryCode: countryCodeOrDefault(tenant.countryCode),
    loginTitle: tenant.loginPageTitle || undefined,
    loginSubtitle: tenant.loginPageSubtitle || undefined,
  };
}
